import { Avatar } from '@/components/Avatar'
import { FormGroupContainer } from '@/components/FormContainer'
import { FormRow } from '@/components/Form'
import {
  CustomSelect,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { useCustomSelectStateFromInfiniteScrollProvider } from '@/components/inputs/infinite-scroll-provider-hook'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { observer } from 'mobx-react-lite'
import type { SearchableSelectViewModel } from '@/infra/SearchableSelectViewModel'
import type { Id } from '@/infra/id'

/**
 * An advisor that can be picked.
 */
export interface AdvisorOption {
  displayName: string
  email: string
  avatar?: string | null
  user: { publicId: Id }
}

export interface AdvisorSelectContainerProps {
  vm: SearchableSelectViewModel<AdvisorOption>
  isDisabled?: boolean
}

function advisorName(advisor: AdvisorOption) {
  return advisor.displayName || advisor.email
}

/**
 * Picker for the advisor who will represent an invited client.
 * An empty choice assigns no one.
 */
export const AdvisorSelectContainer = observer(function AdvisorSelectContainer({
  vm,
  isDisabled,
}: AdvisorSelectContainerProps) {
  const selectProps = useCustomSelectStateFromInfiniteScrollProvider({
    scrollProvider: vm.scrollProvider,
    searchText: vm.searchText.value,
    onSearchTextChange: vm.triggerSearch,
  })

  return (
    <FormRow>
      <FormGroupContainer
        label="Assign to"
        help="The advisor who will represent this client. Existing clients are assigned instead of invited."
      >
        <CustomSelect
          {...selectProps}
          multi={false}
          value={vm.selected ?? undefined}
          placeholder="No one"
          isDisabled={isDisabled}
          itemToString={(advisor) => (advisor ? advisorName(advisor) : '')}
          areItemsEqual={(left, right) =>
            left?.user.publicId === right?.user.publicId
          }
          onChange={vm.select}
          testId="assign-advisor-selector"
          renderItem={(itemProps) => (
            <SingleItemSelectedDefault
              {...itemProps}
              visual={
                <Avatar
                  size={24}
                  subject={{
                    avatar: itemProps.item.avatar,
                    displayName: advisorName(itemProps.item),
                  }}
                />
              }
            />
          )}
          renderSearchItem={({ item, ...itemProps }) => (
            <SearchResultItem
              {...itemProps}
              visual={
                <Avatar
                  size={24}
                  subject={{
                    avatar: item.avatar,
                    displayName: advisorName(item),
                  }}
                />
              }
            >
              {advisorName(item)}
            </SearchResultItem>
          )}
        />
      </FormGroupContainer>
    </FormRow>
  )
})
